import BigNumber from 'bignumber.js';
import nxModule from 'nxModule';
import './transaction-details-builder.service'
import './operation-table-builder.service'
import systemPropertyService from "../../../../react/system/systemPropertyService";
import { canPrintDynamicSoa } from '../../report/reportingToolUtils';
import '../modal/modal-info.component';

const templateUrl = require('./transaction-history.template.html');
nxModule.component('transactionHistory', {
  templateUrl: templateUrl,
  transclude: true,
  bindings: {
    product: '<',
    groups: '<',
    status: '<',
    item: '=',
    productType: '<',
    hideAvailableBalanceAfter: '<',
    hideTotalBalanceAfter: '<',
    profile: '<'
  },
  controller: function (operationTableBuilder, reportModal, systemDateService, branchWorkingDayCache, $filter, transactionHistoryService, operationCodeFormatter, reportService, modalInfo, $location) {
    const that = this;
    const MAX_PRINTABLE_TRANSACTION_COUNT = 100;

    that.transactionHistoryConfig = undefined;
    that.selectedTransactionId = undefined;
    that.selectedDateOption = 'System Date';
    that.item = {};
    that.displayRealServerDateTime = systemPropertyService.getProperty('DISPLAY_REAL_SERVER_DATE_TIME') === 'TRUE';
    that.enableRealServerDateTimeFilter = systemPropertyService.getProperty('SOA_REAL_SERVER_DATE_TIME_FILTER_ENABLED') === 'TRUE';
    that.customerNumber = that.profile?.customerNumber;
    that.customerId = that.profile?.id;
    that.reportName = 'TransactionHistoryReport';
    that.dynamicReportName = 'TransactionHistoryDynamicReport';


    that.initializeDateFilters = async () => {
      // Set default date range parameters to display current month
      if (that.hasFilterAndPrint(that.productType)) {
        await systemDateService.getCurrentUserBranchSystemDate()
          .then(date => {
            that.dateTo = date.clone().format('YYYY-MM-DD');
            that.dateFrom = date.clone().subtract(1, "month").format('YYYY-MM-DD');
          });
      }
    }

    that.setDateOption = (option) => {
      that.selectedDateOption = option;
    };

    that.getStatusLabel = (operation) => {
      return transactionHistoryService.getStatusLabel(operation, that.productType);
    };

    that.hideInlinePanel = () => {
      that.selectedTransactionId = null;
    };

    /** This method adds branchWorkingDay data to the transaction for later use in revert-command.service */
    that.transactionClicked =  async (transaction, $event) => {
      transaction.branchWorkingDay = await branchWorkingDayCache.withParam(transaction.registrationBranchId).toPromise();
      $event.stopPropagation();
      that.selectedTransactionId = transaction.id;
      that.item = transaction;
    };

    that.abs = (number) => {
      var bigNumber = new BigNumber(number);
      bigNumber = bigNumber.abs();
      return parseFloat(bigNumber);
    };

    that.getOperationCode = (operation) => {
      if (!that.product?.id) {
        return;
      }
      return operationCodeFormatter.format(operation, that.product.id);
    };

    that.translate = operation => {
      let code = that.getOperationCode(operation);
      if(operationCodeFormatter.isCustomFeeApplyOnOperation(code)) {
        return operation.feeName;
      } else if (that.productType === 'LOAN') {
        if ('DEPOSIT_CUTOFF_CHECK' === code) {
          code = 'LOAN_LATE_CHECK_PAYMENT';
        } else if (code === 'DEPOSIT_CHECK_LOAN' && operation.attributes['LATE_CHECK']) {
          code = 'DEPOSIT_LATE_CHECK_LOAN';
        }
      }

      return $filter('translate')(code, 'OPERATION_NAME');
    };

    that.resetTransactions = () => {
      that.hideInlinePanel();

      if (that.product && that.product.id) {
        const dateParams = that.selectedDateOption === 'System Date' ? {
          dateFrom: that.dateFrom,
          dateTo: that.dateTo
        }:{
          realTimeServerDateFrom: that.dateFrom,
          realTimeServerDateTo: that.dateTo
        };

        that.transactionHistoryConfig = operationTableBuilder.buildNgTable({
          productId: that.product.id,
          status: that.status,
          groups: that.groups,
          passbookOnly: false,
          ...dateParams
        });
      }
    };

    that.getReportParams = () => {
      let params = {
        productId: that.product.id,
        status: that.status,
        groups: that.groups,
        cif: that.customerNumber
      };

      if (['System Date', 'Realtime Server Date'].includes(that.selectedDateOption)) {
        params.dateFrom = that.dateFrom;
        params.dateTo = that.dateTo;
        params.isSystemDate = (that.selectedDateOption === 'System Date');
      }

      return params;
    };

    that.printTransactions = () => {
      reportModal.display({
        params: that.getReportParams(),
        reportCode: that.reportName,
        hideXls: false
      });
    };

    that.downloadTransactions = async () => {
      await reportService.downloadXls({reportCode: that.reportName, params: that.getReportParams()});
    };

    that.canPrintDynamicSoaResult = false;

    that.updateCanPrintDynamicSoa = () => {
      if (that.customerNumber) {
        that.canPrintDynamicSoaResult = canPrintDynamicSoa(that.customerNumber, systemPropertyService);
      } else {
        that.canPrintDynamicSoaResult = false;
      }
    };

    // Decide the label based on total + flag
    that.buttonLabel = () => {
      const total = that.transactionHistoryConfig?.total?.() || 0;
      if (total > MAX_PRINTABLE_TRANSACTION_COUNT) {
        return that.canPrintDynamicSoaResult ? 'Print' : 'Download XLS';
      }
      return 'Print';
    };

    // Decide what to do on click
    that.onPrintOrDownload = async () => {
      const total = that.transactionHistoryConfig?.total?.() || 0;
      if (total > MAX_PRINTABLE_TRANSACTION_COUNT) {
        if (that.canPrintDynamicSoaResult) {
          return that.printTransactionsAsync();
        }
        return that.downloadTransactions();
      }
      return that.printTransactions();
    };

    // Async “print” path when flag is TRUE and total > 100
    that.printTransactionsAsync = async () => {
      try {
        // 1. Call verification endpoint first
        const verification = await reportService.verifyExistingDynamicReport({
            reportCode: that.dynamicReportName,
            params: that.getReportParams()
          });
        const outcome = verification && (verification.data ?? verification);

        // 2. Handle each outcome
        if (outcome === 'NOT_FOUND' || outcome === 'FAILED_RETRYABLE') {
          // No report or failed, queue new report
          const res = await reportService.queueDynamicReport({
            reportCode: that.dynamicReportName,
            params: that.getReportParams()
          });
          const body = res && (res.data ?? res);
          const id = body && body.id;
          if (typeof id === 'number' ? id > 0 : !!id) {
            that.showGenerationInProgressModal();
          }
          return;
        }

        if (outcome === 'ONGOING') {
          // Report is being generated, show info modal
          modalInfo.display(
            [],
            'Bank Statement Generation - In Progress',
            'A statement for the selected date range is already being generated. To track the status, go to Customers → Statements.',
            [
              {
                text: 'Go to Statements',
                action: () => { $location.path(`/customer/${that.customerId}/statements`); },
                bclass: 'btn btn-primary'
              }
            ]
          );
          return;
        }

        if (outcome === 'COMPLETED') {
          // Report exists, ask to regenerate
          modalInfo.display(
            [],
            'Bank Statement Generation - Completed',
            'A statement for the selected date range already exists. Do you want to regenerate the report?',
            [
              {
                text: 'Regenerate',
                action: async () => {
                  // Queue new report
                  const res = await reportService.queueDynamicReport({
                    reportCode: that.dynamicReportName,
                    params: that.getReportParams()
                  });
                  const body = res && (res.data ?? res);
                  const id = body && body.id;
                  if (typeof id === 'number' ? id > 0 : !!id) {
                    that.showGenerationInProgressModal();
                  }
                },
                bclass: 'btn btn-success'
              }
            ]
          );
          return;
        }

        // Fallback: unknown outcome
        modalInfo.display(
          [],
          'Bank Statement Generation',
          'Unable to verify report status. Please try again later.'
        );
      } catch (err) {
        console.error('Error verifying or queuing dynamic report:', err);
        //modal is handled by report service
      }
    };

    that.hasFilterAndPrint = (productType) => {
      return ['ACCOUNT', 'TERM-DEPOSIT'].includes(productType);
    };

    that.showGenerationInProgressModal = () => {
      modalInfo.display(
        [],
        'Bank Statement Generation',
        'Bank Statement generation in progress. To track the status, go to Customers > Statements.',
        [
          {
            text: 'Go to Statements',
            action: () => { $location.path(`/customer/${that.customerId}/statements`); },
            bclass: 'btn btn-primary'
          }
        ]
      );
    };

    that.$onChanges = async function (changes) {
      if (changes.hasOwnProperty('product') && changes.product.currentValue !== null) {
        await that.initializeDateFilters();
        that.resetTransactions();
      }
      if (changes.hasOwnProperty('profile') && changes.profile.currentValue !== null) {
        that.customerNumber = changes.profile.currentValue.customerNumber;
        that.customerId = changes.profile.currentValue.id;
        that.updateCanPrintDynamicSoa();
      }
    };
  }
});
